import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { PRODUCTS } from '@/constants/products.constants';
import { LOGS_ACTION_ID } from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import { orgProductsAtom } from '@/pages/Organizations';
import { isLayoutMobileAtom } from '@/store/layout';

import useStyles from '../../TicketCreateDialog.styles';
import { LOGS_DATA_RESTORE_EXPORT_VALUES } from './ServiceFormLogRestoreExport.constants';

const ServiceFormLogRestoreExport = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [orgProducts] = useAtom(orgProductsAtom);
  const {
    control,
    formState: { errors }
  } = useFormContext<{
    custom_data: {
      logs_data: {
        action: string;
        start_date: Dayjs;
        end_date: Dayjs;
      };
    };
  }>();
  const { classes } = useStyles({ errors });
  const theme = useTheme();

  const [logsAction, logsStartDate] = useWatch({
    control,
    name: ['custom_data.logs_data.action', 'custom_data.logs_data.start_date']
  });

  const retentionProduct = useMemo(() => {
    return orgProducts.filter(
      (product) =>
        product.active === true && product.product_type === PRODUCTS.RET
    );
  }, [orgProducts]);

  const retentionProductDuration = useMemo(() => {
    const hasNinetyDayRetention = retentionProduct.some((product) => {
      return product.retention === '90.0';
    });

    if (hasNinetyDayRetention) return 90;
    return 30;
  }, [retentionProduct]);

  const retentionProductRetentionDate = useMemo(() => {
    return (
      retentionProductDuration &&
      dayjs().subtract(retentionProductDuration, 'days')
    );
  }, [retentionProduct]);

  const retentionStartDateOutOfRange = useMemo(() => {
    return (
      logsStartDate &&
      retentionProductDuration &&
      retentionProductRetentionDate &&
      dayjs(logsStartDate).diff(retentionProductRetentionDate, 'days') < 0
    );
  }, [logsStartDate]);

  return (
    <>
      <Typography component="h2" variant="h6">
        Restore or export log data?
      </Typography>
      <Stack sx={{ mt: '0!important', width: '100%' }}>
        <Controller
          control={control}
          name="custom_data.logs_data.action"
          rules={{
            required: true
          }}
          render={({ field }) => (
            <FormControl sx={{ mt: '0!important' }}>
              <RadioGroup
                name="logs-action-radio-group"
                value={field}
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
              >
                <Stack spacing={1}>
                  <FormControlLabel
                    value="0"
                    checked={!!field.value && field.value.toString() === '0'}
                    control={
                      <Radio
                        sx={{
                          color: errors.custom_data?.logs_data?.action
                            ? theme.vars.palette.error.main
                            : 'inherit',
                          marginTop: theme.spacing(-0.2)
                        }}
                      />
                    }
                    label={
                      <>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          {LOGS_DATA_RESTORE_EXPORT_VALUES[0].text}
                        </Typography>
                        <Typography variant="body2">
                          {LOGS_DATA_RESTORE_EXPORT_VALUES[0].sub_text}
                        </Typography>
                      </>
                    }
                    sx={{
                      alignItems: 'flex-start',
                      py: 1
                    }}
                  />
                  <Stack spacing={1}>
                    <FormControlLabel
                      value="1"
                      checked={!!field.value && field.value.toString() === '1'}
                      control={
                        <Radio
                          sx={{
                            color: errors.custom_data?.logs_data?.action
                              ? theme.vars.palette.error.main
                              : 'inherit',
                            marginTop: theme.spacing(-0.2)
                          }}
                        />
                      }
                      label={
                        <>
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {LOGS_DATA_RESTORE_EXPORT_VALUES[1].text}
                          </Typography>
                          <Typography variant="body2">
                            {LOGS_DATA_RESTORE_EXPORT_VALUES[1].sub_text}
                          </Typography>
                        </>
                      }
                      sx={{
                        alignItems: 'flex-start',
                        py: 1
                      }}
                    />
                  </Stack>
                </Stack>
              </RadioGroup>
            </FormControl>
          )}
        />
      </Stack>
      <Box sx={{ pl: 4 }}>
        {logsAction === LOGS_ACTION_ID.RESTORE && (
          <>
            <Typography sx={{ mb: 1 }}>
              Please provide a start and end date for the logs you wish to
              restore.
            </Typography>
            <Stack
              direction={isLayoutMobile ? 'column' : 'row'}
              sx={{ mb: 2, gap: 2 }}
            >
              <div className={classes.ticketCreateDialogFormElement}>
                <InputLabel htmlFor="logs-start-date-picker">
                  Logs from (Start Date)
                </InputLabel>
                <Controller
                  control={control}
                  defaultValue={dayjs()}
                  name="custom_data.logs_data.start_date"
                  rules={{
                    required: true
                  }}
                  render={({ field }) => (
                    <FormControl id="logs-start-date-picker" fullWidth>
                      <DatePicker
                        disableFuture
                        slotProps={{
                          textField: {
                            error:
                              errors.custom_data?.logs_data &&
                              !!errors.custom_data.logs_data.start_date
                          }
                        }}
                        {...field}
                      />
                    </FormControl>
                  )}
                />
              </div>
              <div className={classes.ticketCreateDialogFormElement}>
                <InputLabel htmlFor="logs-end-date-picker">
                  Logs to (End Date)
                </InputLabel>
                <Controller
                  control={control}
                  defaultValue={dayjs()}
                  name="custom_data.logs_data.end_date"
                  rules={{
                    required: true
                  }}
                  render={({ field }) => (
                    <FormControl id="logs-end-date-picker" fullWidth>
                      <DatePicker
                        disableFuture
                        slotProps={{
                          textField: {
                            error:
                              errors.custom_data?.logs_data &&
                              !!errors.custom_data.logs_data.end_date
                          }
                        }}
                        {...field}
                      />
                    </FormControl>
                  )}
                />
              </div>
            </Stack>
          </>
        )}
      </Box>
      {!isEmpty(retentionProduct) &&
        logsAction === LOGS_ACTION_ID.RESTORE &&
        logsStartDate !== null &&
        retentionStartDateOutOfRange && (
          <Alert severity="warning" sx={{ mt: 0 }}>
            {`The requested start-time is before your Hot Storage limit of
            ${retentionProductDuration} days. Please consider revising your
            request. You can still submit your request and a Pondurance team
            member will reach out to discuss available options.`}
          </Alert>
        )}
      <Alert severity="warning" sx={{ mt: 0 }}>
        This ticket category is managed by the Customer Service team. These
        tickets are not monitored 24/7 by the SOC.
      </Alert>
    </>
  );
};

export default ServiceFormLogRestoreExport;
